import moment from 'moment';
import esES from 'antd/es/locale/es_ES';
import enUS from 'antd/es/locale/en_US';
import ptPT from 'antd/es/locale/pt_PT';
import {
    Button, Col, ConfigProvider, DatePicker, DatePickerProps, Form, Input, PageHeader, Row, notification,
} from 'antd';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ReviewsContext } from './context/reviews.context';
import TextEditor from '../../Articles/components/TextEditor';
import InstituteType from '../../../widgets/InstituteType';
import Country from '../../../widgets/Country';
import SearchUser from '../../SearchUser';
import { IUser } from '../../../interfaces';

const UpdateReview = () => {
    const { t, i18n } = useTranslation('admin');
    const { t: tf } = useTranslation('forms', { keyPrefix: 'FORMS' });
    const { t: ti } = useTranslation('forms', { keyPrefix: 'FORMS.INPUTS' });

    const { id } = useParams();
    const {
        reviews, review, setReview, loadAction, errors, updateReview,
    } = useContext(ReviewsContext);

    const navigate = useNavigate();

    const [form] = Form.useForm();

    const onSubmit = (values: any) => {
        const payload = {
            ...values,
        };

        updateReview(id as string, payload);

        if (!loadAction) {
            if (errors.length === 0) {
                notification.success({ message: tf('RESPONSES.SUCCESS') });
                navigate(-1);
            } else {
                notification.error({
                    message: 'Error',
                    description: errors[0] || tf('SOMENTHING_WENT_WRONG'),
                });
            }
        }
    };

    const RULES: any = [
        { required: true, message: tf('RULES.REQUIRED_FIELD') },
    ];

    const RULES_URL: any = [
        { type: 'url', warningOnly: true, message: tf('RULES.INVALID_URL') },
        { type: 'string', min: 6, message: tf('RULES.MIN_LENGTH', { length: 6 }) },
    ];

    const onChangeDate: DatePickerProps['onChange'] = (date, dateString) => {
        form.setFieldsValue({ publishDate: date?.toISOString() });
    };

    const handleSelectUser = (user: IUser) => {
        form.setFieldsValue({
            name: user.name,
            lastName: user.lastName,
            email: user.email,
            country: user.country,
            jobPosition: user.academic.jobPosition,
            institute: user.academic.institute,
            instituteType: user.academic.instituteType,
            image: user?.image,
        });
    };

    const handleSetReview = (review: any) => {
        setReview(review);

        form.setFieldsValue({
            review: review.review,
            title: review.title,
            keyName: review.keyName,
            externalLink: review.externalLink,
            name: review.name,
            lastName: review.lastName,
            email: review.email,
            country: review.country,
            jobPosition: review.jobPosition,
            institute: review.institute,
            instituteType: review.instituteType,
            image: review?.image,
        });

        if (review.publishDate) {
            form.setFieldsValue({
                publishDate: moment(review.publishDate),
            });
        }
    };

    useEffect(() => {
        if (!id) return;
        const review = reviews.find((review: any) => review._id === id);
        if (!review) return;
        handleSetReview(review);
    }, [id]);

    return (
        <div className="articles__edit">
            <Form
                form={form}
                layout="vertical"
                name="add-review"
                onFinish={onSubmit}
            >
                <PageHeader
                    onBack={() => {
                        form.resetFields();
                        setReview(null);
                        navigate(-1);
                    }}
                    className="site-page-header-responsive"
                    title={`Edit review | ${review?.keyName}`}
                    extra={[
                        <Button
                            size="small"
                            type="primary"
                            onClick={() => form.submit()}
                        >
                            {t('UPDATE')}
                        </Button>,
                    ]}
                    footer={(
                        <Row className="full__width" gutter={[24, 28]}>
                            <Col xs={24} xl={12} className="col-left">
                                <div className="services__container none__margin--top none__padding--medium">
                                    <div className="services__container-form">
                                        <Form.Item name="review" hidden>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item name="image" hidden>
                                            <Input />
                                        </Form.Item>

                                        <Form.Item
                                            label={ti('REVIEW.TITLE')}
                                            name="title"
                                            rules={RULES}
                                        >
                                            <Input placeholder={ti('REVIEW.TITLE_PLACEHOLDER')} />
                                        </Form.Item>

                                        <Form.Item
                                            label={ti('REVIEW.DESCRIPTION')}
                                            rules={RULES}
                                        >
                                            <TextEditor
                                                form={form}
                                                contentName="review"
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label={ti('KEY_NAME')}
                                            name="keyName"
                                            rules={RULES}
                                        >
                                            <Input
                                                placeholder={ti('KEY_NAME_PLACEHOLDER')}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label={ti('EXTERNAL_LINK')}
                                            name="externalLink"
                                            rules={RULES_URL}
                                        >
                                            <Input
                                                placeholder={ti('EXTERNAL_LINK_PLACEHOLDER')}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label={ti('PUBLISH_DATE')}
                                            name="publishDate"
                                            help={ti('PUBLISH_DATE_HELP')}
                                        >
                                            <ConfigProvider
                                                locale={
                                                    i18n.language === 'en'
                                                        ? enUS : i18n.language === 'pt'
                                                            ? ptPT : esES
                                                }
                                            >
                                                <DatePicker
                                                    className="full__width"
                                                    onChange={onChangeDate}
                                                    defaultValue={moment(review?.publishDate)}
                                                />
                                            </ConfigProvider>
                                        </Form.Item>

                                    </div>
                                </div>
                            </Col>
                            <Col xs={24} xl={12} className="col-right">
                                <div className="services__container none__margin--top none__padding--medium">
                                    <div className="services__container-form">

                                        <SearchUser
                                            handleSelect={handleSelectUser}
                                        />

                                        <Row gutter={[16, 16]} className="full__width">
                                            <Col span={12} className="col-left">
                                                <Form.Item
                                                    label={ti('NAME')}
                                                    name="name"
                                                    rules={RULES}
                                                >
                                                    <Input
                                                        placeholder={ti('NAME_PLACEHOLDER')}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label={ti('EMAIL')}
                                                    name="email"
                                                    rules={RULES}
                                                >
                                                    <Input
                                                        placeholder={ti('EMAIL_PLACEHOLDER')}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label={ti('ACADEMIC.JOB_POSITION')}
                                                    name="jobPosition"
                                                    rules={RULES}
                                                >
                                                    <Input
                                                        placeholder={ti('ACADEMIC.JOB_POSITION_PLACEHOLDER')}
                                                    />
                                                </Form.Item>
                                                <InstituteType />
                                            </Col>
                                            <Col span={12} className="col-right">
                                                <Form.Item
                                                    label={ti('LAST_NAME')}
                                                    name="lastName"
                                                    rules={RULES}
                                                >
                                                    <Input
                                                        placeholder={ti('LAST_NAME_PLACEHOLDER')}
                                                    />
                                                </Form.Item>
                                                <Country />
                                                <Form.Item
                                                    label={ti('INSTITUTE')}
                                                    name="institute"
                                                    rules={RULES}
                                                >
                                                    <Input
                                                        placeholder={ti('INSTITUTE_PLACEHOLDER')}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}
                />
            </Form>
        </div>
    );
};

export default UpdateReview;
